import React from 'react';
import {
  Box,
  ButtonBase,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

const parseDate = (value) => {
  if (!value) return null;

  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatCenterDateLabel = (value) => {
  const date = parseDate(value);
  if (!date) return value || '';

  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

const formatSideDateLabel = (value) => {
  const date = parseDate(value);
  if (!date) return value || '';

  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
  }).format(date);
};

const formatMenuDateLabel = (value) => {
  const date = parseDate(value);
  if (!date) return value || '';

  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date);
};

const getDateYear = (value) => {
  const date = parseDate(value);
  if (!date) return null;

  return date.getFullYear();
};

export default function CalendarDateNav({
  selectedDate,
  previousDate,
  nextDate,
  previousDates = [],
  nextDates = [],
  onDateChange,
}) {
  const [previousMenuAnchor, setPreviousMenuAnchor] = React.useState(null);
  const [nextMenuAnchor, setNextMenuAnchor] = React.useState(null);

  const hasPreviousDates = Array.isArray(previousDates) && previousDates.length > 0;
  const hasNextDates = Array.isArray(nextDates) && nextDates.length > 0;

  const closeMenus = () => {
    setPreviousMenuAnchor(null);
    setNextMenuAnchor(null);
  };

  const handleDateClick = (date) => {
    closeMenus();
    if (date) {
      onDateChange?.(date);
    }
  };

  if (!selectedDate) {
    return null;
  }

  const previousMenuDates = [...previousDates].reverse();
  const nextMenuDates = [...nextDates];

  return (
    <Box className="dc-date-nav">
      <IconButton
        size="small"
        disabled={!hasPreviousDates}
        className="dc-date-nav__menu-trigger dc-date-nav__menu-trigger--left"
        onClick={(event) => setPreviousMenuAnchor(event.currentTarget)}
      >
        <KeyboardArrowLeftIcon fontSize="small" />
      </IconButton>

      <DateCell
        disabled={!previousDate}
        label={previousDate ? formatSideDateLabel(previousDate) : ''}
        variant="side"
        onClick={() => handleDateClick(previousDate)}
      />

      <DateCell
        selected
        label={formatCenterDateLabel(selectedDate)}
      />

      <DateCell
        disabled={!nextDate}
        label={nextDate ? formatSideDateLabel(nextDate) : ''}
        variant="side"
        onClick={() => handleDateClick(nextDate)}
      />

      <IconButton
        size="small"
        disabled={!hasNextDates}
        className="dc-date-nav__menu-trigger dc-date-nav__menu-trigger--right"
        onClick={(event) => setNextMenuAnchor(event.currentTarget)}
      >
        <KeyboardArrowRightIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={previousMenuAnchor}
        open={Boolean(previousMenuAnchor)}
        onClose={closeMenus}
        PaperProps={{ className: 'dc-date-nav__menu-paper' }}
      >
        <DateMenuItems
          dates={previousMenuDates}
          onDateClick={handleDateClick}
        />
      </Menu>

      <Menu
        anchorEl={nextMenuAnchor}
        open={Boolean(nextMenuAnchor)}
        onClose={closeMenus}
        PaperProps={{ className: 'dc-date-nav__menu-paper' }}
      >
        <DateMenuItems
          dates={nextMenuDates}
          onDateClick={handleDateClick}
        />
      </Menu>
    </Box>
  );
}

function DateMenuItems({ dates = [], onDateClick }) {
  let currentYear = null;

  return dates.map((date) => {
    const year = getDateYear(date);
    const shouldShowYearDivider = year != null && year !== currentYear;

    if (shouldShowYearDivider) {
      currentYear = year;
    }

    return (
      <React.Fragment key={date}>
        {shouldShowYearDivider ? (
          <Box className="dc-date-nav__menu-year">
            <Divider>
              <Chip
                size="small"
                variant="outlined"
                label={year}
              />
            </Divider>
          </Box>
        ) : null}

        <MenuItem
          onClick={() => onDateClick?.(date)}
          className="dc-date-nav__menu-item"
        >
          {formatMenuDateLabel(date)}
        </MenuItem>
      </React.Fragment>
    );
  });
}

function DateCell({
  label,
  selected = false,
  disabled = false,
  variant = '',
  onClick,
}) {
  const className = [
    'dc-date-nav__cell',
    selected ? 'dc-date-nav__cell--selected' : '',
    variant === 'side' ? 'dc-date-nav__cell--side' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const isInteractive = !disabled && !selected && typeof onClick === 'function';

  return (
    <ButtonBase
      disabled={disabled}
      onClick={isInteractive ? onClick : undefined}
      className={className}
      aria-current={selected ? 'date' : undefined}
      tabIndex={selected ? -1 : undefined}
    >
      <span className="dc-date-nav__label">
        {label}
      </span>
    </ButtonBase>
  );
}
